import { useNavigate } from "@tanstack/react-router";
import {
  ContactRound,
  HouseIcon,
  Menu,
  MessageSquare,
  Settings,
  Users,
  User,
  Building2,
  Hospital,
  Briefcase,
  FileText,
  ClipboardPen,
  ListPlus,
  Clock,
  WalletCards,
  Layers,
  Search,
  ShieldAlert,
  Share2,
  CreditCard,
} from "lucide-react";
import { User as UserType } from "@/common/types/user";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import SidebarItem from "@/components/Sidebar/SidebarItem";
import { SidebarCollapseToggle } from "@/components/Sidebar/sidebar-collapse-toggle";
import { useSidebarCollapsed } from "@/components/Sidebar/use-sidebar-collapsed";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { useRouterState } from "@tanstack/react-router";
import BottomBarItem from "../BottomBar/BottomBarItem";
import { BottomBar } from "@/components/BottomBar/bottom-bar";
import { Select as ReactSelect } from "@/components/Selects/select";
import { SingleValue } from "react-select";
import { OrganizationContext } from "@/hooks/use-organization";
import { Storage } from "@/common/utils/storage";
import { useTRPC } from "@/trpc";
import { Permission } from "@api/shared-runtime/enums";
import { buildOrgPath, isUUID } from "@/common/utils/orgRouteUtils";
import { OrganizationTermsGate } from "@/components/settings/organization-terms-gate";

const getSideItems = (organizationBusinessUnitId: string) => [
  {
    icon: <HouseIcon />,
    label: "Home",
    route: buildOrgPath(organizationBusinessUnitId, "/home"),
  },
  {
    icon: <ContactRound />,
    label: "Providers",
    route: buildOrgPath(organizationBusinessUnitId, "/providers"),
    subItems: [
      {
        icon: <User />,
        label: "Provider Directory",
        route: buildOrgPath(organizationBusinessUnitId, "/providers/all"),
        requiredPermission: Permission.ViewJobOffers,
      },
      {
        icon: <Share2 />,
        label: "Shared with Me",
        route: buildOrgPath(organizationBusinessUnitId, "/providers/shared"),
      },
      {
        icon: <Users />,
        label: "Provider Pools",
        route: buildOrgPath(organizationBusinessUnitId, "/providers/pools"),
        requiredPermission: Permission.ViewProviderPools,
      },
      {
        icon: <ShieldAlert />,
        label: "Credentials",
        badgeLabel: "Beta",
        route: buildOrgPath(
          organizationBusinessUnitId,
          "/providers/credentials",
        ),
        requiredAnyPermission: [
          Permission.ViewProviderCredentials,
          Permission.ManageAffiliations,
        ],
      },
    ],
  },
  {
    icon: <MessageSquare />,
    label: "Chats",
    route: buildOrgPath(organizationBusinessUnitId, "/chat"),
  },
  {
    icon: <Briefcase />,
    label: "Jobs",
    route: buildOrgPath(organizationBusinessUnitId, "/jobs"),
    subItems: [
      {
        icon: <ListPlus />,
        label: "Posts",
        route: buildOrgPath(organizationBusinessUnitId, "/jobs/post"),
      },
      {
        icon: <ClipboardPen />,
        label: "Offers",
        route: buildOrgPath(organizationBusinessUnitId, "/jobs/offer"),
      },
      {
        icon: <Search />,
        label: "Provider Search",
        route: buildOrgPath(organizationBusinessUnitId, "/provider-search"),
        requiredPermission: Permission.ProviderSearch,
        requiresProviderSearch: true,
      },
      {
        icon: <Clock />,
        label: "Timesheets",
        route: buildOrgPath(organizationBusinessUnitId, "/jobs/timesheets"),
      },
      {
        icon: <WalletCards />,
        label: "Wage sets",
        route: buildOrgPath(organizationBusinessUnitId, "/jobs/wage-sets"),
        requiredPermission: Permission.ViewJobOffers,
      },
      {
        icon: <Layers />,
        label: "Hiring Process",
        route: buildOrgPath(organizationBusinessUnitId, "/hiring-process"),
        requiresHiringProcess: true,
      },
      {
        icon: <FileText />,
        label: "Forms",
        route: buildOrgPath(organizationBusinessUnitId, "/forms"),
        requiresHiringProcess: true,
      },
    ],
  },

  {
    icon: <Hospital />,
    label: "Create Organization",
    route: "/org/create",
  },

  {
    icon: <Settings />,
    label: "Settings",
    route: buildOrgPath(organizationBusinessUnitId, "/settings"),
    subItems: [
      {
        icon: <Users />,
        label: "Team",
        route: buildOrgPath(organizationBusinessUnitId, "/team"),
        requiredPermission: Permission.ViewTeamMembers,
      },
      {
        icon: <Building2 />,
        label: "Facilities",
        route: buildOrgPath(organizationBusinessUnitId, "/facilities"),
      },
      {
        icon: <CreditCard />,
        label: "Billing",
        route: buildOrgPath(organizationBusinessUnitId, "/billing"),
        requiredPermission: Permission.ViewInvoices,
      },
    ],
  },
];

const getMobileNavItems = (organizationBusinessUnitId: string) => [
  {
    icon: <HouseIcon />,
    label: "Home",
    route: buildOrgPath(organizationBusinessUnitId, "/home"),
  },
  {
    icon: <ContactRound />,
    label: "Providers",
    route: buildOrgPath(organizationBusinessUnitId, "/providers/all"),
  },
  {
    icon: <Briefcase />,
    label: "Jobs",
    route: buildOrgPath(organizationBusinessUnitId, "/jobs"),
  },
  {
    icon: <Hospital />,
    label: "Create Organization",
    route: "/org/create",
  },
  {
    route: buildOrgPath(organizationBusinessUnitId, "/chat"),
    label: "Chats",
    icon: <MessageSquare />,
  },
];

type SelectOption = {
  value: string;
  label: string;
};

export function OrgLayout({
  children,
  showSidebar = true,
  organizations,
  savedOrg,
  savedOrgBusinessUnit,
  user,
}: {
  children: React.ReactNode;
  showSidebar?: boolean;
  organizations: {
    id: string;
    name: string;
    businessUnits: {
      id: string;
      name: string;
      permissions: Permission[];
      enableHiringProcess?: boolean;
      enableDocumentSigning?: boolean;
      enableProviderSearch?: boolean;
      skipJobPostReview?: boolean;
      enablePrimarySourceVerification?: boolean;
      timezoneId?: string;
    }[];
  }[];
  savedOrg?: {
    id: string;
    name: string;
    businessUnits: {
      id: string;
      name: string;
      permissions: Permission[];
      enableHiringProcess?: boolean;
      enableDocumentSigning?: boolean;
      enableProviderSearch?: boolean;
      skipJobPostReview?: boolean;
      enablePrimarySourceVerification?: boolean;
      timezoneId?: string;
    }[];
  };
  savedOrgBusinessUnit?: {
    id: string;
    name: string;
    permissions: Permission[];
    enableHiringProcess?: boolean;
    enableDocumentSigning?: boolean;
    enableProviderSearch?: boolean;
    skipJobPostReview?: boolean;
    enablePrimarySourceVerification?: boolean;
    timezoneId?: string;
  };
  user: UserType;
}) {
  const [openSheet, setOpenSheet] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  const router = useRouterState();
  const navigate = useNavigate();
  const chatEnabled = true;

  // Derive org from URL (source of truth), fall back to savedOrg for routes without orgId
  const orgIdFromUrl = useMemo(() => {
    const match = router.location.pathname.match(/^\/org\/([^/]+)/);
    if (match && isUUID(match[1])) {
      return match[1];
    }
    return null;
  }, [router.location.pathname]);

  const urlOrg = useMemo(() => {
    if (orgIdFromUrl) {
      return organizations.find((org) => org.id === orgIdFromUrl);
    }
    return undefined;
  }, [orgIdFromUrl, organizations]);

  const selectedOrg = urlOrg ||
    savedOrg || {
      id: "",
      name: "",
      businessUnits: [] as {
        id: string;
        name: string;
        permissions: Permission[];
        enableHiringProcess?: boolean;
        enableDocumentSigning?: boolean;
        enableProviderSearch?: boolean;
        skipJobPostReview?: boolean;
        enablePrimarySourceVerification?: boolean;
        timezoneId?: string;
      }[],
    };

  const buIdFromUrl = useMemo(() => {
    const match = router.location.pathname.match(/^\/org\/bu\/([^/]+)/);
    if (match && isUUID(match[1])) {
      return match[1];
    }
    return null;
  }, [router.location.pathname]);

  // Track manually-selected BU id (within the current org)
  const [manualBuId, setManualBuId] = useState<string | null>(null);

  // Reset manual selection when org changes
  const prevOrgIdRef = React.useRef(selectedOrg.id);
  if (prevOrgIdRef.current !== selectedOrg.id) {
    prevOrgIdRef.current = selectedOrg.id;
    setManualBuId(null);
  }

  // Derive BU synchronously — no stale state on org switch
  const selectedOrgBusinessUnit = useMemo(() => {
    const emptyBu = {
      id: "",
      name: "",
      permissions: [] as Permission[],
      enableHiringProcess: false,
      enableDocumentSigning: false,
      enableProviderSearch: false,
      skipJobPostReview: false,
      enablePrimarySourceVerification: false,
      timezoneId: "America/New_York",
    };
    if (!selectedOrg.id || !selectedOrg.businessUnits?.length) {
      return emptyBu;
    }

    // The URL's BU wins so pages always query the BU they are showing. A
    // manual pick can go stale when the URL changes without the switcher
    // (back button, in-app links to another BU).
    if (buIdFromUrl) {
      const urlBu = selectedOrg.businessUnits.find(
        (bu) => bu.id === buIdFromUrl,
      );
      if (urlBu) {
        return urlBu;
      }
    }

    // If user manually selected a BU in this org, use it
    if (manualBuId) {
      const manualBu = selectedOrg.businessUnits.find(
        (bu) => bu.id === manualBuId,
      );
      if (manualBu) {
        return manualBu;
      }
    }

    // Otherwise use saved BU if it belongs to this org, else first BU
    if (savedOrgBusinessUnit) {
      const savedBu = selectedOrg.businessUnits.find(
        (bu) => bu.id === savedOrgBusinessUnit.id,
      );
      if (savedBu) {
        return savedBu;
      }
    }

    return selectedOrg.businessUnits[0] || emptyBu;
  }, [
    selectedOrg.id,
    selectedOrg.businessUnits,
    buIdFromUrl,
    manualBuId,
    savedOrgBusinessUnit,
  ]);

  const sideItems = useMemo(
    () => getSideItems(selectedOrgBusinessUnit.id),
    [selectedOrgBusinessUnit.id],
  );
  const mobileNavItems = useMemo(
    () => getMobileNavItems(selectedOrgBusinessUnit.id),
    [selectedOrgBusinessUnit.id],
  );

  // Always call this hook to ensure consistent hook count regardless of showSidebar
  const trpc = useTRPC();
  const { data: unreadMessagesCount } = useQuery(
    trpc.chat.getUnreadMessagesCount.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      },
      {
        enabled: !!selectedOrgBusinessUnit?.id,
      },
    ),
  );

  const { data: myPoolAccess } = useQuery(
    trpc.organization.getMyProviderPoolAccess.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      },
      {
        enabled: !!selectedOrgBusinessUnit?.id,
      },
    ),
  );

  const [isPoolAccessDialogOpen, setIsPoolAccessDialogOpen] = useState(false);

  const chatNotificationCount =
    unreadMessagesCount?.reduce(
      (acc, message) => acc + message.unreadCount,
      0,
    ) ?? 0;

  const organizationOptions = useMemo<SelectOption[]>(() => {
    return organizations.map((org) => ({
      value: org.id,
      label: org.name,
    }));
  }, [organizations]);

  const selectedOrgOption = useMemo(() => {
    if (!selectedOrg?.id) {
      return null;
    }

    return (
      organizationOptions.find((option) => option.value === selectedOrg.id) ??
      null
    );
  }, [organizationOptions, selectedOrg?.id]);

  const businessUnitOptions = useMemo<SelectOption[]>(() => {
    return (selectedOrg?.businessUnits ?? []).map((businessUnit) => ({
      value: businessUnit.id,
      label: businessUnit.name,
    }));
  }, [selectedOrg]);

  const selectedBusinessUnitOption = useMemo(() => {
    if (!selectedOrgBusinessUnit?.id) {
      return null;
    }

    return (
      businessUnitOptions.find(
        (option) => option.value === selectedOrgBusinessUnit.id,
      ) ?? null
    );
  }, [businessUnitOptions, selectedOrgBusinessUnit?.id]);

  // Persist business unit selection to localStorage (org is persisted by $orgId.tsx from URL)
  useEffect(() => {
    if (selectedOrgBusinessUnit.id) {
      Storage.setItem("selectedOrgBusinessUnitId", selectedOrgBusinessUnit.id);
    }
  }, [selectedOrgBusinessUnit.id]);

  const hasPermission = (permission: Permission): boolean => {
    if (user.is_admin) {
      return true;
    }
    return selectedOrgBusinessUnit.permissions?.includes(permission) ?? false;
  };

  const getNavItems = (organizationsLength: number) => {
    const baseItems =
      organizationsLength >= 1
        ? sideItems.filter((item) => item.label !== "Create Organization")
        : sideItems
            .filter((item) =>
              ["Home", "Providers", "Create Organization"].includes(item.label),
            )
            .map((item) => {
              const withSubs = item as typeof item & {
                subItems?: {
                  icon: React.ReactNode;
                  label: string;
                  route: string;
                }[];
              };
              return {
                ...item,
                subItems: withSubs.subItems?.filter(
                  (subItem) => subItem.label === "Shared with Me",
                ),
              };
            });

    type SubItemWithFlags = {
      icon: React.ReactNode;
      label: string;
      route: string;
      requiredPermission?: Permission;
      requiredAnyPermission?: Permission[];
      requiresHiringProcess?: boolean;
      requiresProviderSearch?: boolean;
    };

    // Filter items and subItems based on required permissions
    return baseItems
      .map((item) => {
        const withSubs = item as typeof item & {
          subItems?: SubItemWithFlags[];
        };
        if (!withSubs.subItems) {
          return withSubs;
        }
        return {
          ...item,
          subItems: withSubs.subItems.filter(
            (sub: SubItemWithFlags) =>
              (!sub.requiredPermission ||
                hasPermission(sub.requiredPermission)) &&
              (!sub.requiredAnyPermission ||
                sub.requiredAnyPermission.some(hasPermission)) &&
              (!sub.requiresHiringProcess ||
                selectedOrgBusinessUnit.enableHiringProcess) &&
              (!sub.requiresProviderSearch ||
                selectedOrgBusinessUnit.enableProviderSearch),
          ),
        };
      })
      .filter((item) => {
        const withFlags = item as typeof item & {
          requiredPermission?: Permission;
          requiresHiringProcess?: boolean;
          requiresProviderSearch?: boolean;
        };
        if (
          withFlags.requiresHiringProcess &&
          !selectedOrgBusinessUnit.enableHiringProcess
        ) {
          return false;
        }
        if (
          withFlags.requiresProviderSearch &&
          !selectedOrgBusinessUnit.enableProviderSearch
        ) {
          return false;
        }
        if (!withFlags.requiredPermission) {
          return true;
        }
        return hasPermission(withFlags.requiredPermission);
      });
  };

  const getFilteredMobileNavItems = (organizationsLength: number) => {
    const baseItems =
      organizationsLength >= 1
        ? mobileNavItems.filter((item) => item.label !== "Create Organization")
        : mobileNavItems.filter((item) =>
            ["Home", "Providers", "Create Organization"].includes(item.label),
          );

    return chatEnabled
      ? baseItems
      : baseItems.filter((item) => item.label !== "Chats");
  };

  const filteredMobileNavItems = getFilteredMobileNavItems(
    organizations.length,
  );

  const handleOrgChange = (org: {
    id: string;
    name: string;
    businessUnits: {
      id: string;
      name: string;
      permissions: Permission[];
      timezoneId?: string;
    }[];
  }) => {
    const nextBusinessUnit =
      org.businessUnits.find((bu) => bu.id === savedOrgBusinessUnit?.id) ??
      org.businessUnits[0];
    if (!nextBusinessUnit) {
      return;
    }

    const currentPath = router.location.pathname;
    const orgPathMatch =
      currentPath.match(/^\/org\/bu\/[0-9a-f-]+\/(.*)$/) ??
      currentPath.match(/^\/org\/[0-9a-f-]+\/(.*)$/);
    const subPath = orgPathMatch ? `/${orgPathMatch[1]}` : "/home";
    navigate({
      to: buildOrgPath(nextBusinessUnit.id, subPath),
    });
  };

  const handleBusinessUnitChange = (businessUnit: {
    id: string;
    name: string;
    permissions: Permission[];
  }) => {
    setManualBuId(businessUnit.id);
    const currentPath = router.location.pathname;
    const orgPathMatch =
      currentPath.match(/^\/org\/bu\/[0-9a-f-]+\/(.*)$/) ??
      currentPath.match(/^\/org\/[0-9a-f-]+\/(.*)$/);
    const subPath = orgPathMatch ? `/${orgPathMatch[1]}` : "/home";
    navigate({
      to: buildOrgPath(businessUnit.id, subPath),
    });
  };

  const handleOrgSelectChange = (option: SingleValue<SelectOption>) => {
    if (!option) {
      return;
    }

    const org = organizations.find(
      (organization) => option.value === organization.id,
    );
    if (org) {
      handleOrgChange(org);
    }
  };

  const handleBusinessUnitSelectChange = (
    option: SingleValue<SelectOption>,
  ) => {
    if (!option) {
      return;
    }

    const businessUnit = selectedOrg.businessUnits.find(
      (unit) => unit.id === option.value,
    );
    if (businessUnit) {
      handleBusinessUnitChange(businessUnit);
    }
  };

  const sidebarGridClass = collapsed
    ? "grid md:grid-cols-[72px_1fr] lg:grid-cols-[72px_1fr]"
    : "grid md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]";

  return (
    <OrganizationTermsGate organizationId={selectedOrg.id} user={user}>
      <div
        className={`flex flex-col min-h-dvh max-h-dvh w-full ${showSidebar ? sidebarGridClass : ""}`}
      >
        {showSidebar && (
          <div
            id="static-sidebar"
            className="hidden min-w-0 border-r border-slate-200 bg-white md:block"
          >
            <TooltipProvider>
              <div className="flex h-full min-h-dvh max-h-dvh flex-col gap-2">
                <div
                  className={
                    collapsed
                      ? "flex h-14 shrink-0 items-center justify-center border-b border-slate-200 lg:h-[60px]"
                      : "flex h-14 shrink-0 items-center border-b border-slate-200 px-4 lg:h-[60px] lg:px-6"
                  }
                >
                  <div
                    className={
                      collapsed
                        ? "flex items-center gap-2 font-semibold"
                        : "mr-auto flex items-center gap-2 font-semibold"
                    }
                  >
                    {collapsed ? (
                      <img
                        alt="Saile Logo"
                        src="/static/images/logo_icon.png"
                        className="size-8"
                      />
                    ) : (
                      <img
                        alt="Saile Logo"
                        src="/static/images/logo.png"
                        className="w-[114px]"
                      />
                    )}
                  </div>
                  {!collapsed && (
                    <SidebarCollapseToggle
                      variant="header"
                      collapsed={collapsed}
                      onToggle={toggleCollapsed}
                    />
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <nav
                    aria-label="Main navigation"
                    className="flex flex-col gap-1 px-3 py-4 text-sm"
                  >
                    {getNavItems(organizations.length).map((item) => {
                      const isSelected = router.location.pathname.startsWith(
                        item.route,
                      );
                      const hasSelectedSubItem = item.subItems?.some(
                        (subItem) =>
                          router.location.pathname.startsWith(subItem.route),
                      );
                      return (
                        <React.Fragment key={item.label}>
                          <SidebarItem
                            item={{
                              ...item,
                              route: item.route,
                            }}
                            hasSelectedSubItem={hasSelectedSubItem}
                            variant="primary"
                            collapsed={collapsed}
                            notificationCount={
                              item.label === "Chats"
                                ? chatNotificationCount
                                : undefined
                            }
                          />
                          {item.subItems &&
                            (isSelected || hasSelectedSubItem) && (
                              <div
                                className={
                                  collapsed
                                    ? "flex min-w-0 flex-col gap-1 rounded-xl bg-slate-50"
                                    : "ml-3 flex min-w-0 flex-col gap-1 border-l border-slate-200 pl-2"
                                }
                              >
                                {item.subItems.map((subItem) => (
                                  <SidebarItem
                                    key={subItem.label}
                                    item={subItem}
                                    variant="sub"
                                    collapsed={collapsed}
                                    className={
                                      collapsed
                                        ? "hover:bg-slate-100"
                                        : undefined
                                    }
                                  />
                                ))}
                              </div>
                            )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                </div>
                <SidebarCollapseToggle
                  variant="footer"
                  collapsed={collapsed}
                  onToggle={toggleCollapsed}
                />
              </div>
            </TooltipProvider>
          </div>
        )}
        <div className="flex flex-col min-h-dvh max-h-dvh min-w-0">
          <header
            id="header"
            className="flex items-center gap-4 border-b bg-muted py-2 px-4"
          >
            {showSidebar && (
              <Sheet open={openSheet} onOpenChange={setOpenSheet}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 md:hidden"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  id="sidebar"
                  side="left"
                  className="flex h-auto flex-col rounded-r-2xl border-slate-200 bg-white px-4 pb-[max(1rem,var(--ion-safe-area-bottom,0px))]"
                >
                  <SheetTitle className="sr-only">
                    Organization Navigation
                  </SheetTitle>
                  <img
                    alt="Saile Logo"
                    src="/static/images/logo.png"
                    className="ml-2 w-[114px] shrink-0"
                  />
                  <nav
                    aria-label="Main navigation"
                    className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1 py-1"
                  >
                    {getNavItems(organizations.length).map((item) => {
                      const isSelected = router.location.pathname.startsWith(
                        item.route,
                      );
                      const hasSelectedSubItem = item.subItems?.some(
                        (subItem) =>
                          router.location.pathname.startsWith(subItem.route),
                      );
                      return (
                        <React.Fragment key={item.label}>
                          <SidebarItem
                            onClick={() => setOpenSheet(false)}
                            item={{
                              ...item,
                              route: hasSelectedSubItem
                                ? undefined
                                : item.route,
                            }}
                            hasSelectedSubItem={hasSelectedSubItem}
                            variant="primary"
                            notificationCount={
                              item.label === "Chats"
                                ? chatNotificationCount
                                : undefined
                            }
                          />
                          {item.subItems &&
                            (isSelected || hasSelectedSubItem) && (
                              <div className="ml-3 flex min-w-0 flex-col gap-1 border-l border-slate-200 pl-2">
                                {item.subItems.map((subItem) => (
                                  <SidebarItem
                                    key={subItem.label}
                                    item={subItem}
                                    onClick={() => setOpenSheet(false)}
                                    variant="sub"
                                  />
                                ))}
                              </div>
                            )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            <div className="w-full flex-1 flex flex-wrap items-center gap-2 md:flex-nowrap">
              {organizations.length > 0 && (
                <div className="min-w-0 w-full flex-1 max-w-full sm:flex-none sm:min-w-[180px] sm:max-w-[260px] lg:max-w-[300px]">
                  <ReactSelect<SelectOption, false>
                    instanceId="org-layout-organization-select"
                    value={selectedOrgOption}
                    onChange={handleOrgSelectChange}
                    options={organizationOptions}
                    placeholder="Select organization"
                    isSearchable
                    classNamePrefix="react-select"
                    className="text-base md:text-sm"
                    menuPlacement="auto"
                  />
                </div>
              )}

              {selectedOrg?.businessUnits?.length > 0 && (
                <div className="min-w-0 w-full flex-1 max-w-full sm:flex-none sm:min-w-[180px] sm:max-w-[260px] lg:max-w-[300px]">
                  <ReactSelect<SelectOption, false>
                    instanceId="org-layout-business-unit-select"
                    value={selectedBusinessUnitOption}
                    onChange={handleBusinessUnitSelectChange}
                    options={businessUnitOptions}
                    placeholder="Select business unit"
                    isSearchable
                    classNamePrefix="react-select"
                    className="text-base md:text-sm"
                    menuPlacement="auto"
                  />
                </div>
              )}
            </div>
            {myPoolAccess?.restricted && (
              <>
                <button
                  type="button"
                  onClick={() => setIsPoolAccessDialogOpen(true)}
                  className="flex-shrink-0"
                  title="You are filtered to specific provider pools"
                >
                  <Badge
                    variant="warning"
                    size="small"
                    className="cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <Layers className="h-3 w-3" />
                    Filtered by {myPoolAccess.pools.length} pool
                    {myPoolAccess.pools.length !== 1 ? "s" : ""}
                  </Badge>
                </button>
                <Dialog
                  open={isPoolAccessDialogOpen}
                  onOpenChange={setIsPoolAccessDialogOpen}
                >
                  <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                      <DialogTitle>Your provider pool access</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground mb-3">
                      You can only see providers in the following pools. Contact
                      your admin to change your access.
                    </p>
                    <ul className="space-y-1">
                      {myPoolAccess.pools.map((pool) => (
                        <li
                          key={pool.id}
                          className="flex items-center gap-2 text-sm py-1.5 border-b last:border-0"
                        >
                          <Layers className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          {pool.name}
                        </li>
                      ))}
                    </ul>
                  </DialogContent>
                </Dialog>
              </>
            )}
            <UserAvatar user={user} minimal={true} />
          </header>

          <main className="flex-1 flex flex-col relative overflow-y-auto bg-gray-100">
            <OrganizationContext.Provider
              value={{ selectedOrg, selectedOrgBusinessUnit }}
            >
              {children}
            </OrganizationContext.Provider>
          </main>

          {/* Mobile Bottom Navigation */}
          {showSidebar && (
            <BottomBar className="md:hidden">
              {filteredMobileNavItems.map((item) => (
                <BottomBarItem
                  key={item.label}
                  item={item}
                  notificationCount={
                    item.label === "Chats" ? chatNotificationCount : undefined
                  }
                />
              ))}
            </BottomBar>
          )}
        </div>
      </div>
    </OrganizationTermsGate>
  );
}
